import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { I18nProvider } from "./i18n/i18n";
import { LogoSvgFilter } from "./components/MadafricaLogo";
import Layout from "./components/Layout";
import SectionGuard from "./components/SectionGuard";
import HomePage from "./pages/HomePage";
import ExplorePage from "./pages/ExplorePage";
import ArtistsPage from "./pages/ArtistsPage";
import ArtistProfilePage from "./pages/ArtistProfilePage";
import FeaturedPage from "./pages/FeaturedPage";
import TrackDetailPage from "./pages/TrackDetailPage";
import UploadPage from "./pages/UploadPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import PremiumPage from "./pages/PremiumPage";
import AdvertisersPage from "./pages/AdvertisersPage";
import TrendingCountryPage from "./pages/TrendingCountryPage";
import RisingStarsPage from "./pages/RisingStarsPage";
import LibraryPage from "./pages/LibraryPage";
import PlaylistPage from "./pages/PlaylistPage";
import EventsPage from "./pages/EventsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import AboutPage from "./pages/AboutPage";
import PartnersPage from "./pages/PartnersPage";
import PromoStatsWall from "./pages/PromoStatsWall";
import CitiesMapPage from "./pages/CitiesMapPage";
import ChatPage from "./pages/ChatPage";
import CastingsPage from "./pages/CastingsPage";
import CastingDetailPage from "./pages/CastingDetailPage";
import MessagesPage from "./pages/MessagesPage";
import ChallengePage from "./pages/ChallengePage";
import VolaPage from "./pages/VolaPage";
import AdvertisePage from "./pages/AdvertisePage";
import AdvertiserDashboardPage from "./pages/AdvertiserDashboardPage";
import CampaignWizardPage from "./pages/CampaignWizardPage";
import SurveysPage from "./pages/SurveysPage";
import DatingPage from "./pages/DatingPage";
import ArnakPage from "./pages/ArnakPage";
import ShortsPage from "./pages/ShortsPage";
import TopMadaPage from "./pages/TopMadaPage";
import StudioIALocalPage from "./pages/StudioIALocalPage";
import RadoProductionPage from "./pages/RadoProductionPage";
import ArnakChatPage from "./pages/ArnakChatPage";
import ArnakMessagesPage from "./pages/ArnakMessagesPage";
import VoamadaChatPage from "./pages/VoamadaChatPage";
import VoamadaMessagesPage from "./pages/VoamadaMessagesPage";
import { ArnakAdsListPage, ArnakAdDetailPage, ArnakAdNewPage } from "./pages/ArnakAdsPages";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import LegalPage from "./pages/LegalPage";
import CookieConsent from "./components/CookieConsent";
import { loadAdsenseConfig } from "./lib/adsense";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Si on est sur arnakclan.com (déploiement satellite), tout l'app = page ARNAK uniquement.
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);
  if (isArnakDomain) {
    return (
      <Routes>
        <Route path="/chat" element={<ArnakChatPage />} />
        <Route path="/messages" element={<ArnakMessagesPage />} />
        <Route path="/annonces" element={<ArnakAdsListPage />} />
        <Route path="/annonces/nouvelle" element={<ArnakAdNewPage />} />
        <Route path="/annonces/:id" element={<ArnakAdDetailPage />} />
        <Route path="/rado-production" element={<RadoProductionPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<ArnakPage />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/shorts" element={<ShortsPage />} />
        <Route path="/topmada" element={<TopMadaPage />} />
        <Route path="/studio-ia-local" element={<StudioIALocalPage />} />
        <Route path="/vola" element={<VolaPage />} />
        <Route path="/sondages" element={<SurveysPage />} />
        <Route path="/rencontres" element={<SectionGuard section="dating"><DatingPage /></SectionGuard>} />
        <Route path="/dating" element={<SectionGuard section="dating"><DatingPage /></SectionGuard>} />
        <Route path="/annoncer" element={<AdvertisePage />} />
        <Route path="/annonceur" element={<AdvertiserDashboardPage />} />
        <Route path="/annonceur/campagnes/nouvelle" element={<CampaignWizardPage />} />
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cgu" element={<TermsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/mentions-legales" element={<LegalPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/artists" element={<ArtistsPage />} />
        <Route path="/featured" element={<FeaturedPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/promo" element={<PromoStatsWall />} />
        <Route path="/promo/stats" element={<PromoStatsWall />} />
        <Route path="/carte" element={<CitiesMapPage />} />
        <Route path="/map" element={<CitiesMapPage />} />
        <Route path="/chat" element={<VoamadaChatPage />} />
        <Route path="/trending" element={<SectionGuard section="trending"><TrendingCountryPage /></SectionGuard>} />
        <Route path="/rising" element={<SectionGuard section="rising"><RisingStarsPage /></SectionGuard>} />
        <Route path="/library" element={<SectionGuard section="library"><LibraryPage /></SectionGuard>} />
        <Route path="/playlist/:id" element={<PlaylistPage />} />
        <Route path="/events" element={<SectionGuard section="events"><EventsPage /></SectionGuard>} />
        <Route path="/castings" element={<SectionGuard section="castings"><CastingsPage /></SectionGuard>} />
        <Route path="/casting/:id" element={<CastingDetailPage />} />
        <Route path="/artist/:id" element={<ArtistProfilePage />} />
        <Route path="/track/:id" element={<TrackDetailPage />} />
        <Route path="/upload" element={<SectionGuard section="upload"><UploadPage /></SectionGuard>} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/premium" element={<SectionGuard section="premium"><PremiumPage /></SectionGuard>} />
        <Route path="/advertisers" element={<SectionGuard section="advertisers"><AdvertisersPage /></SectionGuard>} />
        <Route path="/messages" element={<VoamadaMessagesPage />} />
        <Route path="/messages/:id" element={<VoamadaMessagesPage />} />
        <Route path="/defi/:slug" element={<ChallengePage />} />
        <Route path="/defi-cuisine/:slug" element={<ChallengePage />} />
      </Route>
      <Route path="/arnak" element={<ArnakPage />} />
      <Route path="/arnak/chat" element={<ArnakChatPage />} />
      <Route path="/arnak/messages" element={<ArnakMessagesPage />} />
      <Route path="/arnak/annonces" element={<ArnakAdsListPage />} />
      <Route path="/arnak/annonces/nouvelle" element={<ArnakAdNewPage />} />
      <Route path="/arnak/annonces/:id" element={<ArnakAdDetailPage />} />
      <Route path="/annonces" element={<ArnakAdsListPage />} />
      <Route path="/annonces/nouvelle" element={<ArnakAdNewPage />} />
      <Route path="/annonces/:id" element={<ArnakAdDetailPage />} />
      <Route path="/messages" element={<ArnakMessagesPage />} />
      <Route path="/rado-production" element={<RadoProductionPage />} />
      <Route path="/arnak/rado-production" element={<RadoProductionPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
    </Routes>
  );
}

function App() {
  React.useEffect(() => { loadAdsenseConfig(); }, []);
  return (
    <div className="App grain">
      <LogoSvgFilter />
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <PlayerProvider>
              <AppRouter />
              <CookieConsent />
              <Toaster theme="dark" position="top-center" richColors />
            </PlayerProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
